import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAddEcommerceProductMutation } from "../../../features/tenant/ecommerceProduct/ecommerceProductApi";
import {
  useFetchEcommerceTagsMutation,
  useAddEcommerceTagMutation,
} from "../../../features/tenant/ecommerceTags/ecommerceTagsApi";
import { useFetchEcommerceHeirarchyMutation } from "../../../features/tenant/ecommerceProduct/ecommerceHeirarchyApi";
import { useFetchEcommerceSizesMutation } from "../../../features/tenant/ecommerceProduct/ecommerceSizesApi";
import { useFetchEcommerceColorsMutation } from "../../../features/tenant/ecommerceProduct/ecommerceColorsApi";
import { useFetchCompanyProfileMutation } from "../../../features/tenant/companyProfile/companyProfileApi";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { FiX, FiPlus, FiCheck } from "react-icons/fi";

// matrix key
const mk = (sizeId, colorId) => `${sizeId}_${colorId}`;

const DEFAULT_VARIANT_CONFIG = {
  price: ["mrp"],
};

const normalizeVariantConfig = (config) => {
  const configuredPrices = Array.isArray(config?.price)
    ? config.price
        .filter(Boolean)
        .map((value) =>
          `${value}`.endsWith("_price") ? `${value}` : `${value}_price`,
        )
    : [];

  return {
    price: [
      "mrp",
      ...configuredPrices.filter((field) => field !== "mrp" && field !== "mrp_price"),
    ],
  };
};

const fieldLabel = (field) =>
  field.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const createVariantCell = (priceFields, defaultMrp = "") => ({
  ...Object.fromEntries(
    priceFields.map((field) => [field, field === "mrp" ? defaultMrp : ""]),
  ),
  moq: "",
  inventory: "",
  enabled: true,
});

// ─── Shared Matrix UI ─────────────────────────────────────────────────────────

function VariantMatrix({
  selectedColors,
  selectedSizes,
  matrix,
  colorOptions,
  sizeOptions,
  colorPickerOpt,
  setColorPickerOpt,
  sizePickerOpt,
  setSizePickerOpt,
  onAddColor,
  onRemoveColor,
  onAddSize,
  onRemoveSize,
  onCellChange,
  onCellToggle,
  // Image props
  colorImages,
  onUploadColorImages,
  onRemoveColorImage,
  isUploadingColorImages,

  priceFields,
}) {
  // ── Bulk Fill State ────────────────────────────────────────────────────────
  const [globalFill, setGlobalFill] = useState({
    mrp: "",
    wsp: "",
    moq: "",
    inventory: "",
  });

  const [colFill, setColFill] = useState({});
  // { colorId: { mrp, wsp, moq, inventory } }

  const [rowFill, setRowFill] = useState({});
  // { sizeId: { mrp, wsp, moq, inventory } }

  const hasFillValue = (fill) =>
    [...priceFields, "moq", "inventory"].some((field) => fill[field]);

  const fillCells = (cells) => {
    cells.forEach((cell) => {
      const { sizeId, colorId, mrp, moq, inventory } = cell;
      const sizeObj = selectedSizes.find((s) => s.id === sizeId);
      const isBundle = sizeObj?.name && sizeObj.name.includes(",");

      priceFields.forEach((field) => {
        if (cell[field] !== undefined && cell[field] !== "") {
          onCellChange(sizeId, colorId, field, cell[field]);
        }
      });

      if (moq !== undefined && moq !== "") {
        onCellChange(sizeId, colorId, "moq", moq);
      }

      if (!isBundle && inventory !== undefined && inventory !== "") {
        onCellChange(sizeId, colorId, "inventory", inventory);
      }
    });
  };

  const handleFillAll = () => {
    if (!hasFillValue(globalFill)) {
      toast.error("Enter MRP, WSP, MOQ or Inventory to fill");
      return;
    }

    const cells = [];

    selectedSizes.forEach((s) =>
      selectedColors.forEach((c) => {
        const cell = matrix[mk(s.id, c.id)];

        if (cell?.enabled !== false) {
          cells.push({
            sizeId: s.id,
            colorId: c.id,
            ...globalFill,
          });
        }
      }),
    );

    fillCells(cells);

    toast.success(
      `Filled ${cells.length} enabled cell${cells.length !== 1 ? "s" : ""}`,
    );
  };

  const handleFillColumn = (colorId) => {
    const fill = colFill[colorId] || {};

    if (!hasFillValue(fill)) {
      toast.error("Enter MRP, WSP, MOQ or Inventory to fill");
      return;
    }

    const cells = [];

    selectedSizes.forEach((s) => {
      const cell = matrix[mk(s.id, colorId)];

      if (cell?.enabled !== false) {
        cells.push({
          sizeId: s.id,
          colorId,
          ...fill,
        });
      }
    });

    fillCells(cells);
  };

  const handleFillRow = (sizeId) => {
    const fill = rowFill[sizeId] || {};

    if (!hasFillValue(fill)) {
      toast.error("Enter MRP, WSP, MOQ or Inventory to fill");
      return;
    }

    const cells = [];

    selectedColors.forEach((c) => {
      const cell = matrix[mk(sizeId, c.id)];

      if (cell?.enabled !== false) {
        cells.push({
          sizeId,
          colorId: c.id,
          ...fill,
        });
      }
    });

    fillCells(cells);
  };

  const hasMatrix = selectedColors.length > 0 && selectedSizes.length > 0;

  const colorSelectOptions = colorOptions
    .filter((c) => !selectedColors.some((sc) => sc.id === c.id))
    .map((c) => ({
      id: c.id,
      name: c.color_name,
    }));

  const sizeSelectOptions = sizeOptions
    .filter((s) => !selectedSizes.some((ss) => ss.id === s.id))
    .map((s) => ({
      id: s.id,
      name: s.type === "group" ? `${s.name} (Group)` : s.name,
    }));

  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-700 mb-1">
        Color × Size Variant Matrix *
      </h2>

      <p className="text-xs text-gray-400 mb-4">
        Select colors &amp; sizes — matrix auto-generates. Toggle cells off for
        combinations that don't exist. Use fill shortcuts to bulk-update MRP,
        WSP, MOQ &amp; inventory.
      </p>

      {/* ── Selectors ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Color */}
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Colors
          </p>

          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <SelectComponent
                title="Select Color"
                dataArr={colorSelectOptions}
                selected={colorPickerOpt}
                setSelected={setColorPickerOpt}
              />
            </div>

            <button
              type="button"
              onClick={onAddColor}
              className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium mb-1"
            >
              <FiPlus /> Add
            </button>
          </div>

          {selectedColors.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No colors selected</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedColors.map((c) => (
                <span
                  key={c.id}
                  className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pl-1 pr-2 py-0.5 text-xs font-medium text-gray-700 shadow-sm"
                >
                  <span
                    className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
                    style={{
                      backgroundColor: c.hex_code,
                    }}
                  />

                  {c.color_name}

                  <button
                    type="button"
                    onClick={() => onRemoveColor(c.id)}
                    className="text-gray-400 hover:text-red-500 ml-0.5"
                  >
                    <FiX size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Size */}
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Sizes
          </p>

          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <SelectComponent
                title="Select Size"
                dataArr={sizeSelectOptions}
                selected={sizePickerOpt}
                setSelected={setSizePickerOpt}
              />
            </div>

            <button
              type="button"
              onClick={onAddSize}
              className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-medium mb-1"
            >
              <FiPlus /> Add
            </button>
          </div>

          {selectedSizes.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No sizes selected</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedSizes.map((s) => (
                <span
                  key={s.id}
                  className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2 py-0.5 text-xs font-semibold text-emerald-700 shadow-sm"
                >
                  {s.name}

                  <button
                    type="button"
                    onClick={() => onRemoveSize(s.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <FiX size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {!hasMatrix ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
          <p className="text-3xl mb-2">📊</p>

          <p className="text-gray-400 text-sm">
            Add at least one color and one size to see the variant matrix.
          </p>
        </div>
      ) : (
        <>
          {/* ── Global Fill Bar ──────────────────────────────────────────── */}
          <div className="flex items-center gap-3 mb-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl overflow-x-auto whitespace-nowrap">
            <span className="text-xs font-semibold text-indigo-700 flex-shrink-0">
              ⚡ Fill All Enabled
            </span>

            {priceFields.map((field) => (
              <div
                key={field}
                className="flex items-center gap-1 flex-shrink-0"
              >
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {fieldLabel(field)} ₹
                </span>
                <input
                  type="number"
                  value={globalFill[field] || ""}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  onChange={(e) =>
                    setGlobalFill((p) => ({
                      ...p,
                      [field]: e.target.value,
                    }))
                  }
                  className="w-24 h-8 border border-indigo-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
            ))}

            {/* MOQ */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                MOQ
              </span>

              <input
                type="number"
                value={globalFill.moq}
                placeholder="10"
                min="1"
                step="1"
                onChange={(e) =>
                  setGlobalFill((p) => ({
                    ...p,
                    moq: e.target.value,
                  }))
                }
                className="w-24 h-8 border border-indigo-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>

            {/* Inventory */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Inventory
              </span>

              <input
                type="number"
                value={globalFill.inventory}
                placeholder="100"
                min="0"
                onChange={(e) =>
                  setGlobalFill((p) => ({
                    ...p,
                    inventory: e.target.value,
                  }))
                }
                className="w-24 h-8 border border-indigo-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>

            {/* Apply */}
            <button
              type="button"
              onClick={handleFillAll}
              className="flex items-center justify-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 h-8 rounded-lg text-xs font-semibold flex-shrink-0"
            >
              <FiCheck size={12} />
              Apply to All
            </button>

            <span className="text-xs text-gray-400 flex-shrink-0">
              Leave a field empty to skip that field.
            </span>
          </div>

          {/* ── Matrix Table ─────────────────────────────────────────────── */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table
              className="text-sm border-collapse"
              style={{ minWidth: "100%" }}
            >
              <thead>
                {/* Row 1: Color names */}
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase border-r border-gray-200 w-28"
                    rowSpan={3}
                  >
                    Size
                  </th>

                  {selectedColors.map((c) => (
                    <th
                      key={c.id}
                      colSpan={priceFields.length + 3}
                      className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 last:border-r-0"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <span
                          className="w-3.5 h-3.5 rounded-full border border-gray-200 flex-shrink-0"
                          style={{
                            backgroundColor: c.hex_code,
                          }}
                        />

                        {c.color_name}
                      </div>
                    </th>
                  ))}

                  <th
                    className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase"
                    rowSpan={2}
                  >
                    Fill Row →
                  </th>
                </tr>

                {/* Row 2: Column fill controls */}
                <tr className="bg-gray-50 border-b border-gray-100">
                  {selectedColors.map((c) => (
                    <td
                      key={c.id}
                      colSpan={priceFields.length + 3}
                      className="px-2 py-1.5 border-r border-gray-200 last:border-r-0"
                    >
                      <div className="flex items-center gap-1 justify-center">
                        {priceFields.map((field) => (
                          <input
                            key={field}
                            type="number"
                            value={(colFill[c.id] || {})[field] || ""}
                            placeholder={fieldLabel(field)}
                            min="0"
                            step="0.01"
                            onChange={(e) =>
                              setColFill((p) => ({
                                ...p,
                                [c.id]: {
                                  ...(p[c.id] || {}),
                                  [field]: e.target.value,
                                },
                              }))
                            }
                            className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                          />
                        ))}

                        {/* MOQ */}
                        <input
                          type="number"
                          value={(colFill[c.id] || {}).moq || ""}
                          placeholder="MOQ"
                          min="1"
                          step="1"
                          onChange={(e) =>
                            setColFill((p) => ({
                              ...p,
                              [c.id]: {
                                ...(p[c.id] || {}),
                                moq: e.target.value,
                              },
                            }))
                          }
                          className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                        />

                        {/* Inventory */}
                        <input
                          type="number"
                          value={(colFill[c.id] || {}).inventory || ""}
                          placeholder="Inv"
                          min="0"
                          onChange={(e) =>
                            setColFill((p) => ({
                              ...p,
                              [c.id]: {
                                ...(p[c.id] || {}),
                                inventory: e.target.value,
                              },
                            }))
                          }
                          className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                        />

                        <button
                          type="button"
                          onClick={() => handleFillColumn(c.id)}
                          title="Fill this column"
                          className="text-xs bg-indigo-500 hover:bg-indigo-600 text-white px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
                        >
                          ↓ Fill
                        </button>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Row 3: Sub-labels */}
                <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-400 font-medium">
                  {selectedColors.map((c) => (
                    <React.Fragment key={c.id}>
                      {priceFields.map((field) => (
                        <th
                          key={field}
                          className="px-2 py-1.5 text-center whitespace-nowrap"
                        >
                          {fieldLabel(field)} (₹)*
                        </th>
                      ))}

                      <th className="px-2 py-1.5 text-center whitespace-nowrap">
                        MOQ
                      </th>

                      <th className="px-2 py-1.5 text-center whitespace-nowrap">
                        Inventory
                      </th>

                      <th className="px-2 py-1.5 text-center border-r border-gray-200 last:border-r-0 text-gray-300">
                        On
                      </th>
                    </React.Fragment>
                  ))}

                  <th className="px-2 py-1.5 text-center text-xs text-gray-400">
                    {priceFields.map(fieldLabel).join(" / ")} / MOQ / Inv /
                    Apply
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* ── Variant Rows ───────────────────────────────────────── */}
                {selectedSizes.map((size, sIdx) => (
                  <tr
                    key={size.id}
                    className={`border-b border-gray-100 ${
                      sIdx % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                    }`}
                  >
                    {/* Size label */}
                    <td className="px-3 py-2.5 border-r border-gray-200 w-28">
                      <span className="inline-flex items-center justify-center bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                        {size.name}
                      </span>
                    </td>

                    {/* Cells per color */}
                    {selectedColors.map((color) => {
                      const cell =
                        matrix[mk(size.id, color.id)] ||
                        createVariantCell(priceFields);

                      const enabled = cell.enabled !== false;

                      return (
                        <React.Fragment key={color.id}>
                          {priceFields.map((field) => (
                            <td key={field} className="px-2 py-2">
                              {enabled ? (
                                <input
                                  type="number"
                                  value={cell[field] || ""}
                                  onChange={(e) =>
                                    onCellChange(
                                      size.id,
                                      color.id,
                                      field,
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300 text-center"
                                />
                              ) : (
                                <div className="w-full text-center text-xs text-gray-300 bg-gray-50 rounded-lg py-1.5 border border-dashed border-gray-200">
                                  N/A
                                </div>
                              )}
                            </td>
                          ))}

                          {/* MOQ */}
                          <td className="px-2 py-2">
                            {enabled ? (
                              <input
                                type="number"
                                value={cell.moq || ""}
                                onChange={(e) =>
                                  onCellChange(
                                    size.id,
                                    color.id,
                                    "moq",
                                    e.target.value,
                                  )
                                }
                                placeholder="1"
                                min="1"
                                step="1"
                                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300 text-center"
                              />
                            ) : (
                              <div className="w-full text-center text-xs text-gray-300 bg-gray-50 rounded-lg py-1.5 border border-dashed border-gray-200">
                                N/A
                              </div>
                            )}
                          </td>

                          {/* Inventory */}
                          <td className="px-2 py-2">
                            {enabled ? (
                              size.name && size.name.includes(",") ? (
                                <div className="w-full text-center text-[10px] text-gray-400 bg-slate-50 border border-slate-200/60 rounded-lg py-1.5 font-bold select-none leading-normal">
                                  Auto-derived
                                </div>
                              ) : (
                                <input
                                  type="number"
                                  value={cell.inventory || ""}
                                  onChange={(e) =>
                                    onCellChange(
                                      size.id,
                                      color.id,
                                      "inventory",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0"
                                  min="0"
                                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300 text-center"
                                />
                              )
                            ) : (
                              <div className="w-full text-center text-xs text-gray-300 bg-gray-50 rounded-lg py-1.5 border border-dashed border-gray-200">
                                N/A
                              </div>
                            )}
                          </td>

                          {/* Toggle */}
                          <td className="px-1 py-2 border-r border-gray-200 last:border-r-0 text-center">
                            <button
                              type="button"
                              onClick={() => onCellToggle(size.id, color.id)}
                              title={
                                enabled
                                  ? "Disable this combination"
                                  : "Enable this combination"
                              }
                              className={`w-7 h-7 rounded-full flex items-center justify-center mx-auto transition-colors text-xs font-bold ${
                                enabled
                                  ? "bg-emerald-100 text-emerald-600 hover:bg-red-100 hover:text-red-500"
                                  : "bg-gray-100 text-gray-400 hover:bg-emerald-100 hover:text-emerald-600"
                              }`}
                            >
                              {enabled ? (
                                <FiCheck size={12} />
                              ) : (
                                <FiPlus size={12} />
                              )}
                            </button>
                          </td>
                        </React.Fragment>
                      );
                    })}

                    {/* Row fill */}
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1">
                        {priceFields.map((field) => (
                          <input
                            key={field}
                            type="number"
                            value={(rowFill[size.id] || {})[field] || ""}
                            placeholder={fieldLabel(field)}
                            min="0"
                            step="0.01"
                            onChange={(e) =>
                              setRowFill((p) => ({
                                ...p,
                                [size.id]: {
                                  ...(p[size.id] || {}),
                                  [field]: e.target.value,
                                },
                              }))
                            }
                            className="w-14 border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                          />
                        ))}

                        {/* Row MOQ */}
                        <input
                          type="number"
                          value={(rowFill[size.id] || {}).moq || ""}
                          placeholder="MOQ"
                          min="1"
                          step="1"
                          onChange={(e) =>
                            setRowFill((p) => ({
                              ...p,
                              [size.id]: {
                                ...(p[size.id] || {}),
                                moq: e.target.value,
                              },
                            }))
                          }
                          className="w-14 border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                        />

                        {/* Row Inventory */}
                        {size.name && size.name.includes(",") ? (
                          <input
                            type="number"
                            disabled
                            placeholder="🔒"
                            className="w-12 border border-slate-200 bg-slate-50 text-slate-400 rounded px-1.5 py-1 text-xs text-center cursor-not-allowed select-none"
                          />
                        ) : (
                          <input
                            type="number"
                            value={(rowFill[size.id] || {}).inventory || ""}
                            placeholder="Inv"
                            min="0"
                            onChange={(e) =>
                              setRowFill((p) => ({
                                ...p,
                                [size.id]: {
                                  ...(p[size.id] || {}),
                                  inventory: e.target.value,
                                },
                              }))
                            }
                            className="w-12 border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 text-center"
                          />
                        )}

                        <button
                          type="button"
                          onClick={() => handleFillRow(size.id)}
                          title="Fill this row"
                          className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-1.5 py-1 rounded font-medium whitespace-nowrap"
                        >
                          → Fill
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {/* ── Color Images Row ─────────────────────────────────── */}
                <tr className="bg-slate-50 border-t-2 border-gray-200">
                  <td className="px-3 py-4 border-r border-gray-200 align-top">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-bold text-gray-600 uppercase">
                        Images
                      </span>

                      <span className="text-[10px] text-gray-400 leading-tight">
                        At least 1 image required per color
                      </span>
                    </div>
                  </td>

                  {selectedColors.map((color) => {
                    const images = colorImages[color.id] || [];

                    return (
                      <td
                        key={color.id}
                        colSpan={priceFields.length + 3}
                        className="px-3 py-3 border-r border-gray-200 last:border-r-0 align-top"
                      >
                        <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                          {/* Color heading */}
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0"
                                style={{
                                  backgroundColor: color.hex_code,
                                }}
                              />

                              <div>
                                <p className="text-xs font-semibold text-gray-700">
                                  {color.color_name}
                                </p>

                                <p
                                  className={`text-[10px] ${
                                    images.length > 0
                                      ? "text-emerald-600"
                                      : "text-red-500"
                                  }`}
                                >
                                  {images.length > 0
                                    ? `${images.length} image${
                                        images.length !== 1 ? "s" : ""
                                      } uploaded`
                                    : "At least 1 image required"}
                                </p>
                              </div>
                            </div>

                            {/* Upload button */}
                            <label
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                                isUploadingColorImages[color.id]
                                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
                              }`}
                            >
                              <FiPlus size={12} />

                              {isUploadingColorImages[color.id]
                                ? "Uploading..."
                                : "Upload Images"}

                              <input
                                type="file"
                                className="hidden"
                                multiple
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                disabled={isUploadingColorImages[color.id]}
                                onChange={(e) => {
                                  onUploadColorImages(color.id, e.target.files);

                                  e.target.value = null;
                                }}
                              />
                            </label>
                          </div>

                          {/* Images */}
                          {images.length === 0 ? (
                            <div className="border-2 border-dashed border-red-200 bg-red-50 rounded-lg p-4 text-center">
                              <p className="text-xs text-red-500 font-medium">
                                No images uploaded for {color.color_name}
                              </p>

                              <p className="text-[10px] text-red-400 mt-1">
                                Upload at least one image to continue
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                              {images.map((img, imageIndex) => (
                                <div
                                  key={`${img}-${imageIndex}`}
                                  className="relative group"
                                >
                                  <img
                                    src={img}
                                    alt={`${color.color_name} ${
                                      imageIndex + 1
                                    }`}
                                    className="w-full h-20 object-cover rounded-lg border border-gray-200"
                                  />

                                  <button
                                    type="button"
                                    onClick={() =>
                                      onRemoveColorImage(color.id, imageIndex)
                                    }
                                    className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-90 group-hover:opacity-100 transition-opacity"
                                    title="Remove image"
                                  >
                                    <FiX size={12} />
                                  </button>

                                  <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded">
                                    {imageIndex + 1}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}

                  {/* Row fill spacer */}
                  <td className="px-2 py-3" />
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
              <FiCheck size={10} /> green
            </span>{" "}
            = active combination &nbsp;|&nbsp;
            <span className="font-medium text-gray-400">
              <FiPlus size={10} className="inline" /> gray
            </span>{" "}
            = disabled (N/A, won't be sent) &nbsp;|&nbsp; Click the circle
            button to toggle.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Add Product ──────────────────────────────────────────────────────────────

const AddEcommerceProduct = () => {
  const navigate = useNavigate();

  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenantName = vendorData.tenant_name;
  const tenant_id = vendorData.tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [formData, setFormData] = useState({
    product_name: "",
    article_no: "",
    mrp: "",
    tags: [],
    category_id: null,
  });

  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [matrix, setMatrix] = useState({});

  // Color ID -> uploaded image URLs
  //
  // Example:
  // {
  //   1: ["red-1.jpg", "red-2.jpg"],
  //   2: ["blue-1.jpg"]
  // }
  const [colorImages, setColorImages] = useState({});

  // Color ID -> uploading state
  const [isUploadingColorImages, setIsUploadingColorImages] = useState({});

  const [colorPickerOpt, setColorPickerOpt] = useState(null);
  const [sizePickerOpt, setSizePickerOpt] = useState(null);
  const [newTagName, setNewTagName] = useState("");

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [variantConfig, setVariantConfig] = useState(DEFAULT_VARIANT_CONFIG);

  const [
    addProduct,
    {
      data: addProductData,
      isLoading: addProductIsLoading,
      error: addProductError,
    },
  ] = useAddEcommerceProductMutation();

  const [fetchCategories, { data: categoriesData }] =
    useFetchEcommerceHeirarchyMutation();

  const [fetchTags, { data: tagsData }] = useFetchEcommerceTagsMutation();

  const [fetchSizes] = useFetchEcommerceSizesMutation();
  const [fetchColors] = useFetchEcommerceColorsMutation();
  const [fetchCompanyProfile] = useFetchCompanyProfileMutation();

  const [addTag, { data: addTagData, error: addTagError }] =
    useAddEcommerceTagMutation();

  const [uploadSingleFile] = useUploadSingleFileMutation();

  // ── Initial Data ────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCategories({
      token,
      tenant_id,
    });

    fetchTags({
      slug: tenant_id,
      token,
    });

    fetchSizes({
      slug: tenant_id,
      token,
    })
      .unwrap()
      .then((r) => {
        if (r?.body) {
          setSizeOptions(r.body.filter((s) => s.status === "1"));
        }
      });

    fetchColors({
      slug: tenant_id,
      token,
    })
      .unwrap()
      .then((r) => {
        if (r?.body) {
          setColorOptions(r.body.filter((c) => c.status === "1"));
        }
      });

    fetchCompanyProfile({
      tenant_id,
      token,
    })
      .unwrap()
      .then((r) => {
        const company = r?.body?.data || r?.body;
        const ecommerceProducts = company?.ecommerce_products;

        if (ecommerceProducts) {
          setVariantConfig(normalizeVariantConfig(ecommerceProducts));
        }
      });
  }, []);

  useEffect(() => {
    if (categoriesData?.body) {
      const d = Array.isArray(categoriesData.body)
        ? categoriesData.body
        : categoriesData.body.data || [];

      setCategories(
        d.map((c) => ({
          id: c.id,
          name: c.name,
        })),
      );
    }
  }, [categoriesData]);

  useEffect(() => {
    if (tagsData?.body) {
      const d = Array.isArray(tagsData.body)
        ? tagsData.body
        : tagsData.body.data || [];

      setTags(
        d.map((t) => ({
          id: t.id,
          name: t.name,
        })),
      );
    }
  }, [tagsData]);

  useEffect(() => {
    if (addTagData?.body) {
      toast.success("Tag created");
      setNewTagName("");

      fetchTags({
        slug: tenant_id,
        token,
      });
    }
  }, [addTagData]);

  useEffect(() => {
    if (addTagError) {
      toast.error(addTagError.data?.message || "Failed to create tag");
    }
  }, [addTagError]);

  useEffect(() => {
    if (addProductData) {
      toast.success("Product added");

      navigate(`/genefied/${tenantName}/ecommerce-products`);
    }
  }, [addProductData]);

  useEffect(() => {
    if (addProductError) {
      toast.error(addProductError.data?.message || "Failed to add product");
    }
  }, [addProductError]);

  // ── Matrix Handlers ─────────────────────────────────────────────────────

  const handleAddColor = () => {
    if (!colorPickerOpt) {
      toast.error("Please select a color");
      return;
    }

    const colorObj = colorOptions.find((c) => c.id === colorPickerOpt.id);

    if (!colorObj || selectedColors.some((c) => c.id === colorObj.id)) {
      toast.error("Color already added");
      return;
    }

    setSelectedColors((p) => [
      ...p,
      {
        id: colorObj.id,
        color_name: colorObj.color_name,
        hex_code: colorObj.hex_code,
      },
    ]);

    setMatrix((p) => {
      const u = { ...p };

      selectedSizes.forEach((s) => {
        const k = mk(s.id, colorObj.id);

        if (!u[k]) {
          u[k] = createVariantCell(variantConfig.price, formData.mrp || "");
        }
      });

      return u;
    });

    // Initialize empty image collection for the color.
    setColorImages((p) => ({
      ...p,
      [colorObj.id]: p[colorObj.id] || [],
    }));

    setColorPickerOpt(null);
  };

  const handleRemoveColor = (colorId) => {
    setSelectedColors((p) => p.filter((c) => c.id !== colorId));

    setMatrix((p) => {
      const u = { ...p };

      selectedSizes.forEach((s) => {
        delete u[mk(s.id, colorId)];
      });

      return u;
    });

    // Remove all images belonging to the removed color.
    setColorImages((p) => {
      const updated = { ...p };
      delete updated[colorId];
      return updated;
    });
  };

  const handleAddSize = () => {
    if (!sizePickerOpt) {
      toast.error("Please select a size");
      return;
    }

    const sizeObj = sizeOptions.find((s) => s.id === sizePickerOpt.id);

    if (!sizeObj || selectedSizes.some((s) => s.id === sizeObj.id)) {
      toast.error("Size already added");
      return;
    }

    setSelectedSizes((p) => [
      ...p,
      {
        id: sizeObj.id,
        name: sizeObj.name,
        type: sizeObj.type,
      },
    ]);

    setMatrix((p) => {
      const u = { ...p };

      selectedColors.forEach((c) => {
        const k = mk(sizeObj.id, c.id);

        if (!u[k]) {
          u[k] = createVariantCell(variantConfig.price, formData.mrp || "");
        }
      });

      return u;
    });

    setSizePickerOpt(null);
  };

  const handleRemoveSize = (sizeId) => {
    setSelectedSizes((p) => p.filter((s) => s.id !== sizeId));

    setMatrix((p) => {
      const u = { ...p };

      selectedColors.forEach((c) => {
        delete u[mk(sizeId, c.id)];
      });

      return u;
    });
  };

  const handleCellChange = (sizeId, colorId, field, value) => {
    const k = mk(sizeId, colorId);

    setMatrix((p) => ({
      ...p,
      [k]: {
        ...(p[k] || createVariantCell(variantConfig.price, formData.mrp || "")),
        [field]: value,
      },
    }));
  };

  const handleCellToggle = (sizeId, colorId) => {
    const k = mk(sizeId, colorId);

    setMatrix((p) => ({
      ...p,
      [k]: {
        ...(p[k] || createVariantCell(variantConfig.price, formData.mrp || "")),
        enabled: !(p[k]?.enabled ?? true),
      },
    }));
  };

  // ── Color Image Handlers ────────────────────────────────────────────────

  const handleUploadColorImages = async (colorId, files) => {
    if (!files || files.length === 0) {
      return;
    }

    const fileArray = Array.from(files);

    setIsUploadingColorImages((p) => ({
      ...p,
      [colorId]: true,
    }));

    try {
      for (const file of fileArray) {
        const fd = new FormData();

        fd.append("image", file);

        try {
          const response = await uploadSingleFile({
            token,
            body: fd,
          }).unwrap();

          const fileLink = response?.body?.fileLink;

          if (fileLink) {
            setColorImages((p) => ({
              ...p,
              [colorId]: [...(p[colorId] || []), fileLink],
            }));
          } else {
            toast.error(`Failed to upload ${file.name}`);
          }
        } catch (error) {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    } finally {
      setIsUploadingColorImages((p) => ({
        ...p,
        [colorId]: false,
      }));
    }
  };

  const handleRemoveColorImage = (colorId, imageIndex) => {
    setColorImages((p) => ({
      ...p,
      [colorId]: (p[colorId] || []).filter((_, index) => index !== imageIndex),
    }));
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }

    if (!formData.article_no.trim()) {
      toast.error("Article number is required");
      return;
    }

    if (
        !formData.mrp || formData.mrp <= 0
    ) {
      toast.error("Valid default MRP is required");
      return;
    }

    if (!formData.category_id) {
      toast.error("Category is required");
      return;
    }

    if (selectedColors.length === 0) {
      toast.error("Please add at least one color");
      return;
    }

    if (selectedSizes.length === 0) {
      toast.error("Please add at least one size");
      return;
    }

    // ── Validate images per color ────────────────────────────────────────
    const colorsWithoutImages = selectedColors.filter(
      (color) => !colorImages[color.id] || colorImages[color.id].length === 0,
    );

    if (colorsWithoutImages.length > 0) {
      if (colorsWithoutImages.length === 1) {
        toast.error(
          `Please upload at least one image for ${colorsWithoutImages[0].color_name}`,
        );
      } else {
        toast.error(
          `Please upload images for: ${colorsWithoutImages
            .map((c) => c.color_name)
            .join(", ")}`,
        );
      }

      return;
    }

    const sizesPayload = [];

    for (const size of selectedSizes) {
      for (const color of selectedColors) {
        const cell = matrix[mk(size.id, color.id)] || {};

        // Skip disabled cells.
        if (cell.enabled === false) continue;

        // Validate configured price fields.
        for (const priceField of variantConfig.price) {
          if (!cell[priceField] || parseFloat(cell[priceField]) <= 0) {
            toast.error(
              `${fieldLabel(
                priceField,
              )} required for Size: ${size.name} / Color: ${color.color_name}`,
            );

            return;
          }
        }

        // Validate MOQ.
        if (!cell.moq || parseFloat(cell.moq) <= 0) {
          toast.error(
            `MOQ required for Size: ${size.name} / Color: ${color.color_name}`,
          );

          return;
        }

        const isBundle = size.name && size.name.includes(",");

        sizesPayload.push({
          size_id: size.id,
          color_id: color.id,

          ...Object.fromEntries(
            variantConfig.price.map((field) => [
              field,
              parseFloat(cell[field]),
            ]),
          ),

          moq: parseFloat(cell.moq),

          inventory: isBundle ? 0 : parseInt(cell.inventory) || 0,
        });
      }
    }

    if (sizesPayload.length === 0) {
      toast.error("At least one size-color combination must be enabled");

      return;
    }

    // ── Convert colorImages state to API payload ─────────────────────────
    //
    // Frontend state:
    //
    // {
    //   1: ["red-1.jpg", "red-2.jpg"],
    //   2: ["blue-1.jpg"]
    // }
    //
    // API payload:
    //
    // images: [
    //   {
    //     color_id: 1,
    //     urls: ["red-1.jpg", "red-2.jpg"]
    //   },
    //   {
    //     color_id: 2,
    //     urls: ["blue-1.jpg"]
    //   }
    // ]

    const imagesPayload = selectedColors.map((color) => ({
      color_id: color.id,
      color_name: color.color_name,
      urls: colorImages[color.id] || [],
    }));

    addProduct({
      slug: tenant_id,
      token,

      product_name: formData.product_name,
      article_no: formData.article_no,

      mrp: parseFloat(formData.mrp),

      category_id: formData.category_id?.id?.toString(),

      sizes: sizesPayload,

      tags: formData.tags?.map((t) => t.id) || [],

      images: imagesPayload,
    });
  };

  // Transform size master options to SelectComponent compatible array
  const selectSizeOptions = sizeOptions
    .filter((s) => s.status === "1")
    .map((s) => ({
      id: s.id,
      name: s.name,
    }));

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Add Ecommerce Product
        </h1>

        <button
          type="button"
          onClick={() => navigate(`/genefied/${tenantName}/ecommerce-products`)}
          className="text-blue-600 hover:text-blue-800 mt-2 text-sm"
        >
          ← Back to Products
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputComponent
              name="Product Name"
              type="text"
              value={formData.product_name}
              setValue={(v) =>
                setFormData({
                  ...formData,
                  product_name: v,
                })
              }
              placeholder="Enter product name"
              required
            />

            <InputComponent
              name="Article Number"
              type="text"
              value={formData.article_no}
              setValue={(v) =>
                setFormData({
                  ...formData,
                  article_no: v,
                })
              }
              placeholder="Enter article number"
              required
            />

            {
              <InputComponent
                name="Default MRP"
                type="number"
                value={formData.mrp}
                setValue={(v) =>
                  setFormData({
                    ...formData,
                    mrp: v,
                  })
                }
                placeholder="Used to pre-fill the matrix"
                required
              />
            }

            <SelectComponent
              title="Ecommerce Category"
              dataArr={categories}
              selected={formData.category_id}
              setSelected={(v) =>
                setFormData({
                  ...formData,
                  category_id: v,
                })
              }
            />
          </div>

          {/* Matrix + Color Images */}
          <VariantMatrix
            selectedColors={selectedColors}
            selectedSizes={selectedSizes}
            matrix={matrix}
            colorOptions={colorOptions}
            sizeOptions={sizeOptions}
            priceFields={variantConfig.price}
            colorPickerOpt={colorPickerOpt}
            setColorPickerOpt={setColorPickerOpt}
            sizePickerOpt={sizePickerOpt}
            setSizePickerOpt={setSizePickerOpt}
            onAddColor={handleAddColor}
            onRemoveColor={handleRemoveColor}
            onAddSize={handleAddSize}
            onRemoveSize={handleRemoveSize}
            onCellChange={handleCellChange}
            onCellToggle={handleCellToggle}
            colorImages={colorImages}
            onUploadColorImages={handleUploadColorImages}
            onRemoveColorImage={handleRemoveColorImage}
            isUploadingColorImages={isUploadingColorImages}
          />

          {/* Tags */}
          <div>
            <MultiSelectComponent
              title="Tags"
              dataArr={tags}
              selected={formData.tags}
              setSelected={(v) =>
                setFormData({
                  ...formData,
                  tags: v,
                })
              }
            />

            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Create new tag"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />

              <button
                type="button"
                onClick={() => {
                  if (newTagName.trim()) {
                    addTag({
                      slug: tenant_id,
                      token,
                      name: newTagName.trim(),
                    });
                  }
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
              >
                Create Tag
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() =>
                navigate(`/genefied/${tenantName}/ecommerce-products`)
              }
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={addProductIsLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addProductIsLoading ? "Adding..." : "Add Product"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEcommerceProduct;
